'use client'

import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { usePathname } from 'next/navigation';
import { useMenu } from '@/context/MenuContext';
import { LocaleSwitcher } from '@/components/layout/locale-switcher';
import { useMainNavigationItems } from '@/constants/navigation-items';
import { Backdrop } from './Backdrop';
import { BackButton } from '../sidebar/BackButton';
import { MainNavigator } from './MainNavigator';
import { SubNavigator } from './SubNavigator';
import { MobileFooter } from './MobileFooter';
import { LogoLink } from './LogoLink';

interface NavItem {
  name: string;
  href: string;
  subItems?: NavItem[];
}

const MobileNavigation = () => {
  const { mobileMenuOpen, setMobileMenuOpen } = useMenu();
  const navigation = useMainNavigationItems();
  const pathname = usePathname();

  const [activeSidebarSubNav, setActiveSidebarSubNav] = useState<NavItem | null>(null);

  const mobileNavigationHandler = () => {
    setMobileMenuOpen(false);
    setActiveSidebarSubNav(null);
  };

  const sidebarVariants = {
    hidden: { x: '100%' },
    visible: { x: 0 },
    exit: { x: '100%' },
  };

  const linkVariants = {
    hidden: { opacity: 0, x: 50 },
    visible: (i: number) => ({
      opacity: 1,
      x: 0,
      transition: {
        delay: i * 0.1,
        duration: 0.3,
      },
    }),
    exit: { opacity: 0, x: 50 },
  };

  const backButton = {
    hidden: { opacity: 0, x: 50 },
    visible: () => ({
      opacity: 1,
      x: 0,
      transition: {
        delay: 0.5,
        duration: 0.3,
      },
    }),
    exit: { opacity: 0, x: 50 },
  };

  return (
    <AnimatePresence>
      {mobileMenuOpen && (
        <>
          {/* Backdrop */}
          <Backdrop mobileNavigationHandler={setMobileMenuOpen} />
          {/* Sidebar */}
          <motion.div
            className='fixed inset-y-0 right-0 z-50 h-dvh w-full overflow-hidden bg-dark-900 px-6 py-8 sm:max-w-sm sm:ring-1 sm:ring-neutral-900/10'
            initial='hidden'
            animate='visible'
            exit='exit'
            variants={sidebarVariants}
            transition={{
              duration: 0.7,
              ease: [0.25, 0.1, 0.25, 1],
            }}
          >
            <LogoLink mobileNavigationHandler={mobileNavigationHandler} />
            <div className='mt-10 flow-root'>
              <div className=''>
                <AnimatePresence mode='wait'>
                  {activeSidebarSubNav ? (
                    // Subview
                    <motion.div key='subview' initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 50 }} transition={{ duration: 0.3 }}>
                      <div className='space-y-4'>
                        {activeSidebarSubNav.subItems?.map((sub, i) => (
                          <SubNavigator key={sub.name} pathname={pathname} sub={sub} i={i} mobileNavigationHandler={mobileNavigationHandler} />
                        ))}
                      </div>
                      <motion.div key='back' initial='hidden' animate='visible' exit='exit' variants={backButton} className='mt-6'>
                        <BackButton setActiveSidebarSubNav={setActiveSidebarSubNav} />
                      </motion.div>
                    </motion.div>
                  ) : (
                    // Main view
                    <motion.div key='mainview' initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 50 }} transition={{ duration: 0.3 }} className='space-y-4'>
                      {navigation.map((item, i) => (
                        <MainNavigator key={item.name} pathname={pathname} item={item} i={i} setActiveSidebarSubNav={setActiveSidebarSubNav} mobileNavigationHandler={mobileNavigationHandler} />
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              <div className='h-[0.2px] bg-neutral-700 my-10' />
              <motion.div custom={navigation.length + 1} initial='hidden' animate='visible' exit='exit' variants={linkVariants} className='flex px-3'>
                <LocaleSwitcher />
              </motion.div>
            </div>
            <MobileFooter />
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default MobileNavigation;
